import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useDisclosure } from '@chakra-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import _reject from 'lodash/reject';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from '../../../../models/hooks';
import { FollowedRecruiters } from '../PositionType';

export type PositionFollowButtonProps = {
  recruiterId: string;
  followedRecruiters: FollowedRecruiters[];
  watchedRecruiters: FollowedRecruiters[];
  positionId: string;
  callbackWatchedRecruiters: (recruiters: FollowedRecruiters[], positionId: string) => void;
  notifyUpdatingStateToParent: (status: boolean, positionId: string) => void;
};

const PositionWatchButtonComponent = ({
  recruiterId,
  followedRecruiters,
  watchedRecruiters,
  positionId,
  callbackWatchedRecruiters,
  notifyUpdatingStateToParent,
}: PositionFollowButtonProps): JSX.Element => {
  const [isUpdatingWatch, setUpdatingWatch] = useState<boolean>(false);
  const updatePositionStatusOfRecruiter = useStoreActions(
    (actions) => actions.positionManagement.updatePositionStatusOfRecruiter,
  );

  const { t } = useTranslation();
  const { isOpen: isOpenWarnLastRecruiter, onClose: onCloseWarnLastRecruiter, onOpen: onOpenWarnLastRecruiter } = useDisclosure();
  const {
    isOpen: isOpenWarnToUnFollowPosition,
    onClose: onCloseWarnToUnFollowPosition,
    onOpen: onOpenWarnToUnFollowPosition,
  } = useDisclosure();

  const isWatched = useMemo(() => watchedRecruiters.some((value) => value.id === recruiterId), [watchedRecruiters, recruiterId]);
  const isFollowing = useMemo(() => followedRecruiters.some((value) => value.id === recruiterId), [
    followedRecruiters,
    recruiterId,
  ]);

  const updatePositionFollow = useCallback(async () => {
    setUpdatingWatch(true);
    notifyUpdatingStateToParent(true, positionId);
    try {
      if (!isWatched) {
        await updatePositionStatusOfRecruiter({ positionId, recruiterId, status: 'watched' });
        callbackWatchedRecruiters([...watchedRecruiters, { id: recruiterId, name: '' }], positionId);
      } else {
        await updatePositionStatusOfRecruiter({ positionId, recruiterId, status: 'none' });
        callbackWatchedRecruiters(_reject(watchedRecruiters, { id: recruiterId }), positionId);
      }
    } finally {
      setUpdatingWatch(false);
      notifyUpdatingStateToParent(false, positionId);
    }
  }, [
    watchedRecruiters,
    callbackWatchedRecruiters,
    isWatched,
    notifyUpdatingStateToParent,
    positionId,
    recruiterId,
    updatePositionStatusOfRecruiter,
  ]);

  const handleButtonClick = useCallback(async () => {
    try {
      if (isWatched) {
        onOpenWarnToUnFollowPosition();
      } else if (isFollowing && followedRecruiters.length === 1) {
        onOpenWarnLastRecruiter();
      } else {
        await updatePositionFollow();
      }
    } catch (error) {
      /* istanbul ignore next */
      console.error(error || 'Error updating watch position status');
    }
  }, [isWatched, isFollowing, onOpenWarnLastRecruiter, onOpenWarnToUnFollowPosition, updatePositionFollow, followedRecruiters]);

  const handleUnfollowConfirmation = useCallback(async () => {
    await updatePositionFollow();
    onCloseWarnToUnFollowPosition();
    onCloseWarnLastRecruiter();
  }, [onCloseWarnLastRecruiter, onCloseWarnToUnFollowPosition, updatePositionFollow]);

  const handleWarnedUnfollowConfirmation = useCallback(async () => {
    await updatePositionFollow();
    onCloseWarnToUnFollowPosition();
    onCloseWarnLastRecruiter();
  }, [onCloseWarnLastRecruiter, onCloseWarnToUnFollowPosition, updatePositionFollow]);

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        width="100%"
        style={{ borderRadius: 'unset', background: 'unset' }}
        isLoading={isUpdatingWatch}
        onClick={handleButtonClick}
        data-testid={`UpdateWatchBtn-${positionId}`}
      >
        {isWatched ? t('jobPosition:watch.unwatch') : t('jobPosition:watch.watch')}
      </Button>
      <Modal isOpen={isOpenWarnLastRecruiter} onClose={onCloseWarnLastRecruiter} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('jobPosition:watchModal.header')}</ModalHeader>
          <ModalBody>{t('jobPosition:watchModal.body')}</ModalBody>
          <ModalFooter mt={2}>
            <Button data-testid="unfollowModalConfirmBtn" colorScheme="blue" mr={3} onClick={handleUnfollowConfirmation}>
              {t('common:button.okay')}
            </Button>
            <Button data-testid="unfollowModalCloseBtn" colorScheme="red" onClick={onCloseWarnLastRecruiter}>
              {t('common:button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenWarnToUnFollowPosition} onClose={onCloseWarnToUnFollowPosition} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('jobPosition:unwatchWarningModal.header')}</ModalHeader>
          <ModalFooter mt={2}>
            <Button
              data-testid="warnedUnfollowModalConfirmBtn"
              colorScheme="blue"
              mr={3}
              onClick={handleWarnedUnfollowConfirmation}
            >
              {t('common:button.okay')}
            </Button>
            <Button data-testid="warnedUnfollowModalCloseBtn" colorScheme="red" onClick={onCloseWarnToUnFollowPosition}>
              {t('common:button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const PositionWatchButton = React.memo(PositionWatchButtonComponent);
